// src/components/fundraising/FundRaisingPage.tsx
// DROP 18d — Public fund raising page with Paystack
// Open to all — no login required

import React, { useState, useEffect } from "react";
import {
  Heart, Target, Gift, Users, Globe, Crown,
  CheckCircle, AlertTriangle, Loader2, Share2,
  ChevronRight, Star, MessageSquare, Zap
} from "lucide-react";

interface Campaign {
  id: string;
  title: string;
  description: string;
  type: "general"|"event"|"item"|"village";
  targetAmount: number;
  raisedAmount: number;
  currency: string;
  deadline?: string;
  isActive: boolean;
  imageUrl?: string;
  itemsList?: string[];
}

interface Donor {
  name: string;
  amount: number;
  currency: string;
  message?: string;
  village?: string;
  type: "indigene"|"diaspora"|"friend"|"organisation";
  isAnonymous: boolean;
  donatedAt: string;
}

const GIVING_TIERS = [
  { label:"₦500",    amount:500,    currency:"NGN", desc:"Small but mighty" },
  { label:"₦1,000",  amount:1000,   currency:"NGN", desc:"Festival supporter" },
  { label:"₦5,000",  amount:5000,   currency:"NGN", desc:"Community champion" },
  { label:"₦10,000", amount:10000,  currency:"NGN", desc:"Festival patron" },
  { label:"₦50,000", amount:50000,  currency:"NGN", desc:"Bronze sponsor" },
  { label:"Custom",  amount:0,      currency:"NGN", desc:"Set your own amount" },
];

const DONOR_TYPES = [
  { id:"indigene",     label:"Uzairue Indigene",  icon:"👑" },
  { id:"diaspora",     label:"Diaspora",          icon:"✈️" },
  { id:"friend",       label:"Friend of Uzairue", icon:"🤝" },
  { id:"organisation", label:"Organisation",      icon:"🏢" },
];

const CURRENCIES = [
  { code:"NGN", symbol:"₦", flag:"🇳🇬" },
  { code:"USD", symbol:"$", flag:"🇺🇸" },
  { code:"GBP", symbol:"£", flag:"🇬🇧" },
];

export default function FundRaisingPage() {
  const [campaigns,   setCampaigns]   = useState<Campaign[]>([]);
  const [donors,      setDonors]      = useState<Donor[]>([]);
  const [selected,    setSelected]    = useState<Campaign|null>(null);
  const [view,        setView]        = useState<"campaigns"|"give"|"pledge"|"wall">("campaigns");

  // Give form
  const [tier,        setTier]        = useState(GIVING_TIERS[1]);
  const [customAmt,   setCustomAmt]   = useState("");
  const [currency,    setCurrency]    = useState("NGN");
  const [donorName,   setDonorName]   = useState("");
  const [donorEmail,  setDonorEmail]  = useState("");
  const [donorType,   setDonorType]   = useState("indigene");
  const [donorVillage,setDonorVillage]= useState("");
  const [message,     setMessage]     = useState("");
  const [isAnon,      setIsAnon]      = useState(false);
  const [dedicated,   setDedicated]   = useState("");
  const [payLoading,  setPayLoading]  = useState(false);
  const [donated,     setDonated]     = useState(false);
  const [donateRef,   setDonateRef]   = useState("");
  const [errors,      setErrors]      = useState<Record<string,string>>({});

  // Pledge form
  const [pledgeType,  setPledgeType]  = useState<"amount"|"item">("amount");
  const [pledgeAmt,   setPledgeAmt]   = useState("");
  const [pledgeItem,  setPledgeItem]  = useState("");
  const [pledgeDate,  setPledgeDate]  = useState("");
  const [pledgeName,  setPledgeName]  = useState("");
  const [pledgeEmail, setPledgeEmail] = useState("");
  const [pledgeDone,  setPledgeDone]  = useState(false);
  const [pledgeLoading,setPledgeLoading]=useState(false);

  useEffect(() => {
    fetch("/api/fundraising/campaigns")
      .then(r=>r.json())
      .then(d=>{ if(d.success) setCampaigns(d.campaigns||[]); });
    fetch("/api/fundraising/donors")
      .then(r=>r.json())
      .then(d=>{ if(d.success) setDonors(d.donors||[]); });
    // Load Paystack
    if(!document.getElementById("paystack-script")){
      const s=document.createElement("script");
      s.id="paystack-script"; s.src="https://js.paystack.co/v1/inline.js";
      document.head.appendChild(s);
    }
  },[]);

  const actualAmount = tier.amount === 0 ? Number(customAmt)||0 : tier.amount;

  const validateGive = () => {
    const e: Record<string,string> = {};
    if (!donorEmail.trim() || !/\S+@\S+\.\S+/.test(donorEmail)) e.donorEmail = "Valid email required";
    if (!isAnon && !donorName.trim()) e.donorName = "Name required (or choose anonymous)";
    if (actualAmount < 100) e.amount = "Minimum donation is ₦100";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const initiateDonation = async () => {
    if (!validateGive()) return;
    setPayLoading(true);
    try {
      const res = await fetch("/api/fundraising/initiate", {
        method:"POST",
        headers:{ "Content-Type":"application/json" },
        body: JSON.stringify({
          campaignId: selected?.id || "general",
          amount: actualAmount, currency,
          donorName: isAnon ? "Anonymous" : donorName,
          donorEmail, donorType, donorVillage,
          message, isAnonymous: isAnon,
          dedication: dedicated,
        })
      });
      const d = await res.json();
      if (!d.success) { setErrors({ submit:d.error||"Failed" }); setPayLoading(false); return; }

      const handler = (window as any).PaystackPop?.setup({
        key: d.publicKey,
        email: donorEmail,
        amount: actualAmount * 100,
        currency,
        ref: d.reference,
        metadata: {
          payment_type: "donation",
          donor_name: isAnon ? "Anonymous" : donorName,
          campaign_id: selected?.id || "general",
          donor_type: donorType,
          message,
        },
        callback: async (response: { reference:string }) => {
          const vRes = await fetch("/api/fundraising/verify",{
            method:"POST",
            headers:{"Content-Type":"application/json"},
            body:JSON.stringify({ reference:response.reference, donationId:d.donationId })
          });
          const vData = await vRes.json();
          if(vData.success){ setDonateRef(response.reference); setDonated(true); }
          else setErrors({ submit:"Payment received but verification pending — check email" });
        },
        onClose: () => setPayLoading(false),
      });
      handler?.openIframe();
    } catch { setErrors({ submit:"Payment error — please try again" }); }
    finally { setPayLoading(false); }
  };

  const submitPledge = async () => {
    if(!pledgeName||!pledgeEmail){ setErrors({pledge:"Name and email required"}); return; }
    setPledgeLoading(true);
    try {
      const res = await fetch("/api/fundraising/pledge",{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          campaignId:selected?.id||"general",
          pledgeType, amount:Number(pledgeAmt)||0,
          item:pledgeItem, fulfillBy:pledgeDate,
          name:pledgeName, email:pledgeEmail,
        })
      });
      const d = await res.json();
      if(d.success) setPledgeDone(true);
      else setErrors({pledge:d.error||"Failed"});
    } catch { setErrors({pledge:"Error — please try again"}); }
    finally { setPledgeLoading(false); }
  };

  const fmt = (n:number, cur="NGN") =>
    new Intl.NumberFormat("en-NG",{style:"currency",currency:cur,maximumFractionDigits:0}).format(n);

  const totalRaised = donors.reduce((s,d)=>s+d.amount,0);

  // ── CAMPAIGNS VIEW ──────────────────────────────────────────────
  if (view === "campaigns") return (
    <div className="min-h-screen py-12 px-4" style={{ background:"#070D0A" }}>
      <div className="max-w-4xl mx-auto">

        {/* Hero */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center border-2"
                 style={{ borderColor:"#C9A84C", background:"rgba(201,168,76,0.1)" }}>
              <Heart size={28} style={{ color:"#C9A84C" }}/>
            </div>
          </div>
          <h1 className="text-4xl font-bold mb-3" style={{ color:"#EDE8DC", fontFamily:"Cinzel, serif" }}>
            Support ERUE Festival 2026
          </h1>
          <p className="text-lg mb-2" style={{ color:"rgba(237,232,220,0.6)", fontFamily:"Playfair Display, serif" }}>
            "One People, One Kingdom, One Destiny"
          </p>
          <p className="text-sm max-w-xl mx-auto" style={{ color:"rgba(237,232,220,0.4)" }}>
            Help make the Annual ERUE Festival of Uzairue Kingdom a magnificent celebration.
            Every contribution — large or small — makes a difference.
          </p>

          {/* Total raised banner */}
          {totalRaised > 0 && (
            <div className="inline-flex items-center gap-3 mt-6 px-6 py-3 rounded-2xl"
                 style={{ background:"rgba(201,168,76,0.1)", border:"1px solid rgba(201,168,76,0.2)" }}>
              <Zap size={16} style={{ color:"#C9A84C" }}/>
              <span className="text-sm" style={{ color:"rgba(237,232,220,0.7)" }}>Total raised so far:</span>
              <span className="text-lg font-bold" style={{ color:"#C9A84C" }}>{fmt(totalRaised)}</span>
              <span className="text-xs" style={{ color:"rgba(237,232,220,0.4)" }}>by {donors.length} supporters</span>
            </div>
          )}
        </div>

        {/* Action cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          {[
            { icon:<Heart size={22}/>,   title:"Donate Now",    desc:"Make an instant donation via Paystack. NGN, USD or GBP accepted.", action:()=>{ setSelected(null); setView("give"); }, cta:"Donate →", color:"#C9A84C" },
            { icon:<Gift size={22}/>,    title:"Make a Pledge", desc:"Commit to contribute later, or pledge specific items for the festival.", action:()=>{ setSelected(null); setView("pledge"); }, cta:"Pledge →", color:"#60A5FA" },
            { icon:<Users size={22}/>,   title:"Donor Wall",    desc:"See the generous supporters who have contributed to ERUE Festival.", action:()=>setView("wall"), cta:"View Wall →", color:"#4ADE80" },
          ].map(({icon,title,desc,action,cta,color}) => (
            <button key={title} onClick={action}
                    className="text-left p-6 rounded-2xl border transition-all group"
                    style={{ borderColor:"#1C2E22", background:"#0E1812" }}
                    onMouseEnter={e=>(e.currentTarget as HTMLElement).style.borderColor="rgba(201,168,76,0.3)"}
                    onMouseLeave={e=>(e.currentTarget as HTMLElement).style.borderColor="#1C2E22"}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                   style={{ background:`${color}15`, color }}>
                {icon}
              </div>
              <h3 className="font-semibold text-sm mb-2" style={{ color:"#EDE8DC" }}>{title}</h3>
              <p className="text-xs mb-4" style={{ color:"rgba(237,232,220,0.4)" }}>{desc}</p>
              <span className="text-xs font-medium" style={{ color }}>{cta}</span>
            </button>
          ))}
        </div>

        {/* Specific campaigns */}
        {campaigns.filter(c=>c.isActive).length > 0 && (
          <div>
            <h2 className="text-lg font-bold mb-4" style={{ color:"#EDE8DC", fontFamily:"Cinzel, serif" }}>
              Active Fund Raising Campaigns
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {campaigns.filter(c=>c.isActive).map(c => {
                const pct = c.targetAmount > 0 ? Math.min((c.raisedAmount/c.targetAmount)*100,100) : 0;
                return (
                  <div key={c.id} className="p-5 rounded-2xl border cursor-pointer transition-all"
                       style={{ borderColor:"#1C2E22", background:"#0E1812" }}
                       onClick={()=>{ setSelected(c); setView("give"); }}
                       onMouseEnter={e=>(e.currentTarget as HTMLElement).style.borderColor="rgba(201,168,76,0.3)"}
                       onMouseLeave={e=>(e.currentTarget as HTMLElement).style.borderColor="#1C2E22"}>
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="font-semibold text-sm" style={{ color:"#EDE8DC" }}>{c.title}</h3>
                      <span className="text-xs px-2 py-0.5 rounded-full"
                            style={{ background:"rgba(201,168,76,0.1)", color:"#C9A84C", border:"1px solid rgba(201,168,76,0.2)" }}>
                        {c.type}
                      </span>
                    </div>
                    <p className="text-xs mb-4" style={{ color:"rgba(237,232,220,0.4)" }}>{c.description}</p>
                    {c.targetAmount > 0 && (
                      <>
                        <div className="h-1.5 rounded-full mb-2" style={{ background:"#1C2E22" }}>
                          <div className="h-full rounded-full" style={{ width:`${pct}%`, background:"#C9A84C" }}/>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span style={{ color:"#C9A84C" }}>{fmt(c.raisedAmount)} raised</span>
                          <span style={{ color:"rgba(237,232,220,0.3)" }}>Goal: {fmt(c.targetAmount)}</span>
                        </div>
                      </>
                    )}
                    <div className="flex items-center justify-between mt-4">
                      <span className="text-xs font-medium" style={{ color:"#C9A84C" }}>Donate to this campaign →</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ── GIVE VIEW ───────────────────────────────────────────────────
  if (view === "give") {
    if (donated) return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background:"#070D0A" }}>
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
               style={{ background:"rgba(201,168,76,0.1)", border:"2px solid rgba(201,168,76,0.3)" }}>
            <Heart size={36} style={{ color:"#C9A84C" }}/>
          </div>
          <h1 className="text-2xl font-bold mb-2" style={{ color:"#EDE8DC", fontFamily:"Cinzel, serif" }}>
            Thank You! 🙏
          </h1>
          <p className="text-sm mb-4" style={{ color:"rgba(237,232,220,0.5)" }}>
            Your generous donation has been received and will help make
            ERUE Festival 2026 a truly memorable celebration.
          </p>
          <div className="p-4 rounded-xl mb-6" style={{ background:"#0E1812", border:"1px solid #1C2E22" }}>
            <p className="text-xs mb-1" style={{ color:"rgba(237,232,220,0.4)" }}>Reference</p>
            <p className="text-sm font-mono font-bold" style={{ color:"#C9A84C" }}>{donateRef}</p>
          </div>
          <p className="text-xs mb-6" style={{ color:"rgba(237,232,220,0.3)" }}>
            "One People, One Kingdom, One Destiny" — Uzairue Kingdom
          </p>
          <div className="flex gap-3 justify-center">
            <button onClick={()=>{ setDonated(false); setView("campaigns"); }}
                    className="px-5 py-2.5 rounded-xl text-sm border"
                    style={{ borderColor:"#1C2E22", color:"rgba(237,232,220,0.5)" }}>
              Back to Campaigns
            </button>
            <button onClick={()=>{
              const text = `I just supported ERUE Festival 2026 in Uzairue Kingdom! Join me in making this celebration unforgettable. 🎉👑 ${window.location.origin}/give #ErueFestival2026 #UzairueKingdom`;
              if(navigator.share) navigator.share({title:"ERUE Festival 2026",text,url:window.location.href});
              else window.open(`https://wa.me/?text=${encodeURIComponent(text)}`,"_blank");
            }} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold"
               style={{ background:"#C9A84C", color:"#070D0A" }}>
              <Share2 size={14}/> Share
            </button>
          </div>
        </div>
      </div>
    );

    return (
      <div className="min-h-screen py-12 px-4" style={{ background:"#070D0A" }}>
        <div className="max-w-lg mx-auto">
          <button onClick={()=>setView("campaigns")} className="flex items-center gap-2 mb-6 text-xs"
                  style={{ color:"rgba(237,232,220,0.4)" }}>
            ← Back to campaigns
          </button>

          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold mb-1" style={{ color:"#EDE8DC", fontFamily:"Cinzel, serif" }}>
              {selected ? `Support: ${selected.title}` : "Donate to ERUE Festival"}
            </h1>
            <p className="text-xs" style={{ color:"rgba(237,232,220,0.4)" }}>
              Secure payment via Paystack · All currencies accepted
            </p>
          </div>

          <div className="space-y-5">
            {/* Currency selector */}
            <div>
              <label className="text-xs uppercase tracking-wider mb-2 block" style={{ color:"rgba(237,232,220,0.4)" }}>
                Currency
              </label>
              <div className="flex gap-2">
                {CURRENCIES.map(c => (
                  <button key={c.code} onClick={()=>setCurrency(c.code)}
                          className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm border transition-all"
                          style={{ background:currency===c.code?"rgba(201,168,76,0.15)":"transparent", borderColor:currency===c.code?"rgba(201,168,76,0.4)":"#1C2E22", color:currency===c.code?"#C9A84C":"rgba(237,232,220,0.4)" }}>
                    {c.flag} {c.code}
                  </button>
                ))}
              </div>
            </div>

            {/* Giving tiers */}
            <div>
              <label className="text-xs uppercase tracking-wider mb-2 block" style={{ color:"rgba(237,232,220,0.4)" }}>
                Select Amount
              </label>
              <div className="grid grid-cols-3 gap-2">
                {GIVING_TIERS.map(t => (
                  <button key={t.label} onClick={()=>setTier(t)}
                          className="p-3 rounded-xl border text-center transition-all"
                          style={{ background:tier.label===t.label?"rgba(201,168,76,0.15)":"#0E1812", borderColor:tier.label===t.label?"rgba(201,168,76,0.4)":"#1C2E22" }}>
                    <div className="font-bold text-sm" style={{ color:tier.label===t.label?"#C9A84C":"#EDE8DC" }}>
                      {t.label}
                    </div>
                    <div className="text-xs mt-0.5" style={{ color:"rgba(237,232,220,0.3)" }}>{t.desc}</div>
                  </button>
                ))}
              </div>
              {tier.amount === 0 && (
                <input type="number" value={customAmt} onChange={e=>setCustomAmt(e.target.value)}
                       placeholder={`Enter amount in ${currency}`}
                       className="w-full mt-2 px-3 py-3 rounded-xl text-sm outline-none"
                       style={{ background:"#0E1812", border:`1px solid ${errors.amount?"rgba(239,68,68,0.5)":"rgba(201,168,76,0.3)"}`, color:"#EDE8DC" }}/>
              )}
              {errors.amount && <p className="text-xs mt-1" style={{ color:"#FCA5A5" }}>⚠ {errors.amount}</p>}
            </div>

            {/* Donor type */}
            <div>
              <label className="text-xs uppercase tracking-wider mb-2 block" style={{ color:"rgba(237,232,220,0.4)" }}>
                I am a...
              </label>
              <div className="grid grid-cols-2 gap-2">
                {DONOR_TYPES.map(dt => (
                  <button key={dt.id} onClick={()=>setDonorType(dt.id)}
                          className="flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm transition-all"
                          style={{ background:donorType===dt.id?"rgba(201,168,76,0.1)":"transparent", borderColor:donorType===dt.id?"rgba(201,168,76,0.3)":"#1C2E22", color:donorType===dt.id?"#C9A84C":"rgba(237,232,220,0.5)" }}>
                    <span>{dt.icon}</span> {dt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Donor info */}
            <div className="space-y-3 p-4 rounded-xl" style={{ background:"#0E1812", border:"1px solid #1C2E22" }}>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={isAnon} onChange={e=>setIsAnon(e.target.checked)} className="accent-yellow-400"/>
                <span className="text-sm" style={{ color:"rgba(237,232,220,0.6)" }}>Donate anonymously</span>
              </label>

              {!isAnon && (
                <div>
                  <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.4)" }}>Your Name *</label>
                  <input value={donorName} onChange={e=>setDonorName(e.target.value)} placeholder="Full name"
                         className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                         style={{ background:"#121E16", border:`1px solid ${errors.donorName?"rgba(239,68,68,0.5)":"#1C2E22"}`, color:"#EDE8DC" }}/>
                  {errors.donorName && <p className="text-xs mt-1" style={{ color:"#FCA5A5" }}>⚠ {errors.donorName}</p>}
                </div>
              )}

              <div>
                <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.4)" }}>Email Address * (for receipt)</label>
                <input value={donorEmail} onChange={e=>setDonorEmail(e.target.value)} placeholder="your@email.com" type="email"
                       className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                       style={{ background:"#121E16", border:`1px solid ${errors.donorEmail?"rgba(239,68,68,0.5)":"#1C2E22"}`, color:"#EDE8DC" }}/>
                {errors.donorEmail && <p className="text-xs mt-1" style={{ color:"#FCA5A5" }}>⚠ {errors.donorEmail}</p>}
              </div>

              {donorType==="indigene" && (
                <div>
                  <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.4)" }}>Village / Community (optional)</label>
                  <input value={donorVillage} onChange={e=>setDonorVillage(e.target.value)}
                         placeholder="e.g. Jattu-Uzairue, Iyamho-Uzairue…"
                         className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                         style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
                </div>
              )}

              <div>
                <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.4)" }}>Message (optional — shown on donor wall)</label>
                <textarea value={message} onChange={e=>setMessage(e.target.value)} rows={2}
                          placeholder="Share a message of support…"
                          className="w-full px-3 py-2 rounded-lg text-sm outline-none resize-none"
                          style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
              </div>

              <div>
                <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.4)" }}>In honour of / Dedication (optional)</label>
                <input value={dedicated} onChange={e=>setDedicated(e.target.value)}
                       placeholder="e.g. In memory of Mama Agbaje…"
                       className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                       style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
              </div>
            </div>

            {errors.submit && (
              <div className="p-3 rounded-xl text-sm flex items-center gap-2"
                   style={{ background:"rgba(239,68,68,0.1)", border:"1px solid rgba(239,68,68,0.3)", color:"#FCA5A5" }}>
                <AlertTriangle size={14}/> {errors.submit}
              </div>
            )}

            <button onClick={initiateDonation} disabled={payLoading}
                    className="w-full py-4 rounded-2xl font-bold text-base flex items-center justify-center gap-2 transition-all"
                    style={{ background:payLoading?"rgba(201,168,76,0.4)":"#C9A84C", color:"#070D0A" }}>
              {payLoading ? <><Loader2 size={18} className="animate-spin"/> Processing…</> :
                           <><Heart size={18}/> Donate {actualAmount>0?fmt(actualAmount,currency):""}</>}
            </button>

            <p className="text-center text-xs" style={{ color:"rgba(237,232,220,0.2)" }}>
              Secured by Paystack · Your payment info is never stored on our servers
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── PLEDGE VIEW ─────────────────────────────────────────────────
  if (view === "pledge") {
    if (pledgeDone) return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background:"#070D0A" }}>
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
               style={{ background:"rgba(74,222,128,0.1)", border:"2px solid rgba(74,222,128,0.3)" }}>
            <CheckCircle size={32} style={{ color:"#4ADE80" }}/>
          </div>
          <h1 className="text-xl font-bold mb-2" style={{ color:"#EDE8DC" }}>Pledge Recorded!</h1>
          <p className="text-sm mb-6" style={{ color:"rgba(237,232,220,0.5)" }}>
            Thank you for your commitment. We will be in touch regarding your pledge fulfilment.
          </p>
          <button onClick={()=>{ setPledgeDone(false); setView("campaigns"); }}
                  className="px-6 py-2.5 rounded-xl text-sm"
                  style={{ background:"#C9A84C", color:"#070D0A" }}>
            Back to Home
          </button>
        </div>
      </div>
    );

    return (
      <div className="min-h-screen py-12 px-4" style={{ background:"#070D0A" }}>
        <div className="max-w-lg mx-auto">
          <button onClick={()=>setView("campaigns")} className="flex items-center gap-2 mb-6 text-xs"
                  style={{ color:"rgba(237,232,220,0.4)" }}>← Back</button>
          <h1 className="text-2xl font-bold mb-6" style={{ color:"#EDE8DC", fontFamily:"Cinzel, serif" }}>
            Make a Pledge
          </h1>

          <div className="space-y-4 p-5 rounded-2xl" style={{ background:"#0E1812", border:"1px solid #1C2E22" }}>
            {/* Pledge type */}
            <div>
              <label className="text-xs uppercase tracking-wider mb-2 block" style={{ color:"rgba(237,232,220,0.4)" }}>Pledge Type</label>
              <div className="flex gap-2">
                {(["amount","item"] as const).map(t => (
                  <button key={t} onClick={()=>setPledgeType(t)}
                          className="flex-1 py-2.5 rounded-lg text-sm border transition-all capitalize"
                          style={{ background:pledgeType===t?"rgba(201,168,76,0.15)":"transparent", borderColor:pledgeType===t?"rgba(201,168,76,0.4)":"#1C2E22", color:pledgeType===t?"#C9A84C":"rgba(237,232,220,0.5)" }}>
                    {t === "amount" ? "💰 Monetary Amount" : "🎁 Specific Item"}
                  </button>
                ))}
              </div>
            </div>

            {pledgeType === "amount" ? (
              <div>
                <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.4)" }}>Pledge Amount (₦)</label>
                <input type="number" value={pledgeAmt} onChange={e=>setPledgeAmt(e.target.value)} placeholder="Amount you pledge to contribute"
                       className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                       style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
              </div>
            ) : (
              <div>
                <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.4)" }}>Item(s) to Pledge</label>
                <textarea value={pledgeItem} onChange={e=>setPledgeItem(e.target.value)} rows={3}
                          placeholder="e.g. 2 sets of PA speakers, Generator (7.5kva), 10 plastic chairs, Canopy set…"
                          className="w-full px-3 py-2 rounded-lg text-sm outline-none resize-none"
                          style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
              </div>
            )}

            <div>
              <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.4)" }}>Expected Fulfilment Date</label>
              <input type="date" value={pledgeDate} onChange={e=>setPledgeDate(e.target.value)}
                     className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                     style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.4)" }}>Your Name *</label>
                <input value={pledgeName} onChange={e=>setPledgeName(e.target.value)} placeholder="Full name"
                       className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                       style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
              </div>
              <div>
                <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.4)" }}>Email *</label>
                <input value={pledgeEmail} onChange={e=>setPledgeEmail(e.target.value)} placeholder="your@email.com" type="email"
                       className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                       style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
              </div>
            </div>

            {errors.pledge && (
              <p className="text-xs" style={{ color:"#FCA5A5" }}>⚠ {errors.pledge}</p>
            )}

            <button onClick={submitPledge} disabled={pledgeLoading}
                    className="w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
                    style={{ background:"#C9A84C", color:"#070D0A" }}>
              {pledgeLoading ? <><Loader2 size={14} className="animate-spin"/>Submitting…</> : <><Gift size={14}/>Submit Pledge</>}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── DONOR WALL VIEW ─────────────────────────────────────────────
  if (view === "wall") return (
    <div className="min-h-screen py-12 px-4" style={{ background:"#070D0A" }}>
      <div className="max-w-4xl mx-auto">
        <button onClick={()=>setView("campaigns")} className="flex items-center gap-2 mb-6 text-xs"
                style={{ color:"rgba(237,232,220,0.4)" }}>← Back</button>
        <h1 className="text-3xl font-bold mb-2 text-center" style={{ color:"#EDE8DC", fontFamily:"Cinzel, serif" }}>
          Donor Wall of Honour
        </h1>
        <p className="text-center text-sm mb-8" style={{ color:"rgba(237,232,220,0.4)" }}>
          Our heartfelt gratitude to all who have supported ERUE Festival 2026
        </p>

        {donors.filter(d=>!d.isAnonymous).length === 0 ? (
          <div className="text-center py-16" style={{ color:"rgba(237,232,220,0.3)" }}>
            <Heart size={40} className="mx-auto mb-4 opacity-30"/>
            <p>Be the first to support ERUE Festival 2026!</p>
            <button onClick={()=>setView("give")} className="mt-4 px-6 py-2.5 rounded-xl text-sm font-semibold"
                    style={{ background:"#C9A84C", color:"#070D0A" }}>
              Donate Now
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {donors.filter(d=>!d.isAnonymous).map((d,i) => (
              <div key={i} className="p-4 rounded-2xl" style={{ background:"#0E1812", border:"1px solid #1C2E22" }}>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm"
                       style={{ background:"rgba(201,168,76,0.15)", color:"#C9A84C" }}>
                    {d.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-sm" style={{ color:"#EDE8DC" }}>{d.name}</p>
                    {d.village && <p className="text-xs" style={{ color:"rgba(237,232,220,0.4)" }}>{d.village}</p>}
                  </div>
                  <span className="ml-auto text-sm font-bold" style={{ color:"#C9A84C" }}>
                    {new Intl.NumberFormat("en-NG",{style:"currency",currency:d.currency||"NGN",maximumFractionDigits:0}).format(d.amount)}
                  </span>
                </div>
                {d.message && (
                  <p className="text-xs italic mt-1" style={{ color:"rgba(237,232,220,0.4)" }}>"{d.message}"</p>
                )}
                <p className="text-xs mt-2" style={{ color:"rgba(237,232,220,0.2)" }}>
                  {new Date(d.donatedAt).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return null;
}
